import { Component, AfterViewInit, ElementRef, HostListener, OnInit, ViewChild, OnDestroy, Renderer2, Inject } from '@angular/core';
import { ActivatedRoute, NavigationEnd, Router, RouterModule } from '@angular/router';
import { filter, map, mergeMap } from 'rxjs';
import { DOCUMENT, CommonModule } from '@angular/common';
import { FaqComponent } from '../../../shared/faq/faq.component';
import { FaqItem } from '../../../models/faq.model';

@Component({
  selector: 'app-data-visualization-skills-2026',
  imports: [CommonModule, RouterModule, FaqComponent],
  standalone: true,
  templateUrl: './data-visualization-skills-2026.component.html',
  styleUrl: './data-visualization-skills-2026.component.scss'
})
export class DataVisualizationSkills2026Component implements OnInit, AfterViewInit, OnDestroy {
  private jsonLdScript: any;

  dataVisualizationFaqs: FaqItem[] = [
    {
      question: 'What skills are required for data visualization in 2026?',
      answer: `Advanced SQL, semantic modeling, Python or JavaScript, AI integration, UX design principles, and data storytelling.`
    },
    {
      question: 'Is AI replacing data analysts?',
      answer: `No. AI automates repetitive tasks but increases demand for strategic designers and decision architects.`
    },
    {
      question: 'How do I become job-ready in data visualization quickly?',
      answer: `Focus on fundamentals (Month 1), modeling (Month 2), and project building (Month 3). Publish work publicly.`
    },
    {
      question: 'What is the most important soft skill for data professionals?',
      answer: `Strategic thinking and the ability to design decision-driven dashboards.`
    }
  ];

  skills = [
    {
      title: 'Advanced SQL & Query Optimization',
      icon: 'assets/resources/blog/data-visualization-skills-2026/toc-icons/AdvancedSQL_Query Optimization.svg'
    },
    {
      title: 'Scripting With Python Or JavaScript',
      icon: 'assets/resources/blog/data-visualization-skills-2026/toc-icons/ScriptingwithPythonorJavaScript.svg'
    },
    {
      title: 'Semantic Modeling & Data Architecture',
      icon: 'assets/resources/blog/data-visualization-skills-2026/toc-icons/SemanticModeling_Data Architecture.svg'
    },
    {
      title: 'AI Integration & Prompt Intelligence',
      icon: 'assets/resources/blog/data-visualization-skills-2026/toc-icons/AI_Integration_Prompt_Intelligence.svg'
    },
    {
      title: 'Strategic Thinking & Decision Design',
      icon: 'assets/resources/blog/data-visualization-skills-2026/toc-icons/StrategicThinking_DecisionDesign.svg'
    },
    {
      title: 'Data Storytelling',
      icon: 'assets/resources/blog/data-visualization-skills-2026/toc-icons/DataStorytelling.svg'
    },
    {
      title: 'UX & Visual Hierarchy',
      icon: 'assets/resources/blog/data-visualization-skills-2026/toc-icons/UX_Visual Hierarchy.svg'
    },
    {
      title: 'Business Domain Awareness',
      icon: 'assets/resources/blog/data-visualization-skills-2026/toc-icons/BusinessDomainAwareness.svg'
    },
    {
      title: 'Automation Over Manual Effort',
      icon: 'assets/resources/blog/data-visualization-skills-2026/toc-icons/AutomationOverManualEffort.svg'
    },
    {
      title: 'Decision Architecture',
      icon: 'assets/resources/blog/data-visualization-skills-2026/toc-icons/DecisionArchitecture.svg'
    }
  ];

  skillDetails = [
    {
      id: 'advanced-sql',
      title: 'Advanced SQL & Query Optimization',
      icon: 'assets/resources/blog/data-visualization-skills-2026/toc-icons/AdvancedSQL_Query Optimization.svg',
      description: `If you want to build powerful visualizations, you must understand data deeply. Advanced SQL for data analysts is no longer optional in 2026.`,
      subText: `Professionals need:`,
      list1: [
        'Complex joins',
        'Window functions',
        'Query optimization',
        'Performance tuning',
        'Understanding of data granularity'
      ],
      footerText: `Advanced SQL allows you to design semantic models that scale. Without this foundation, even the most beautiful dashboard will struggle with performance and accuracy.`,
      image: 'assets/resources/blog/data-visualization-skills-2026/advanced_sql_img.png'
    },

    {
      id: 'scripting',
      title: 'Scripting With Python Or JavaScript',
      icon: 'assets/resources/blog/data-visualization-skills-2026/toc-icons/ScriptingwithPythonorJavaScript.svg',
      description: `Modern data visualization professionals need scripting capabilities and must extend beyond BI tools.`,
      subText: `Python helps with:`,
      list1: [
        'Data cleaning',
        'Automation',
        '<a href="https://www.kalpitatechnologies.com/company-services/data-engineering" target="_blank" class="bold-link">Data engineering</a> tasks',
        'Advanced analytics'
      ],
      midText: `JavaScript supports:`,
      list2: [
        'Custom visualizations',
        'Embedded analytics',
        'Front-end data interaction'
      ],
      footerText: `The key insight? Hybrid technical capability makes professionals more adaptable in AI-driven environments.`,
      image: 'assets/resources/blog/data-visualization-skills-2026/scripting_with_python.png'
    },

    {
      id: 'semantic',
      title: 'Semantic Modeling & Data Architecture',
      icon: 'assets/resources/blog/data-visualization-skills-2026/toc-icons/SemanticModeling_Data Architecture.svg',
      description: `One of the most underrated yet critical skills for 2026 is data modeling. Understanding:`,
      list1: [
        'Star schema vs. snowflake schema',
        'Cardinality',
        'Relationships',
        'Performance optimization',
        'KPI structure'
      ],
      footerText: `Strong semantic modeling in analytics ensures that insights generated are accurate, scalable, and AI-ready. Without modeling, AI-powered dashboards can produce misleading conclusions.`,
      image: 'assets/resources/blog/data-visualization-skills-2026/semantic_modelling.png'
    },

    {
      id: 'ai',
      title: 'AI Integration & Prompt Intelligence',
      icon: 'assets/resources/blog/data-visualization-skills-2026/toc-icons/AI_Integration_Prompt_Intelligence.svg',
      description: `AI is not replacing data professionals; it is reshaping their workflow. AI now helps with:`,
      list1: [
        'Writing DAX or calculated measures',
        'Cleaning datasets',
        'Prototyping dashboards',
        'Suggesting visual types',
        'Natural language queries'
      ],
      midText: `But the real skill lies in:`,
      list2: [
        'Crafting effective prompts',
        'Validating AI outputs',
        'Integrating AI into workflows responsibly'
      ],
      footerText: `The role of AI in data visualization is no longer experimental; it is foundational to modern analytics workflows. While prompt engineering for analysts has become a critical <a href="https://www.kalpitatechnologies.com/tech-blogs/agi-implementation-guide" target="_blank" class="bold-link">AI skill</a>, AI literacy has become a mandatory skill now.`,
      image: 'assets/resources/blog/data-visualization-skills-2026/ai_integration.png'
    },

    {
      id: 'strategy',
      title: 'Strategic Thinking & Decision Design',
      icon: 'assets/resources/blog/data-visualization-skills-2026/toc-icons/StrategicThinking_DecisionDesign.svg',
      description: `This is the biggest mindset shift in 2026. The future belongs to professionals who design decision-driven dashboards, not just data displays. Visualization professionals must think like strategists:`,
      list1: [
        'What business decision does this dashboard support?',
        'What action should stakeholders take?',
        'What metric drives impact?'
      ],
      footerText: `Strategic design separates report builders from insight leaders.`,
      image: 'assets/resources/blog/data-visualization-skills-2026/strategic_thinking.png'
    },

    {
      id: 'storytelling',
      title: 'Data Storytelling',
      icon: 'assets/resources/blog/data-visualization-skills-2026/toc-icons/DataStorytelling.svg',
      description: `Data storytelling is not a buzzword; it’s a career differentiator. Data storytelling skills differentiate strategic professionals from report builders. Many professionals can create reports. Few can answer:`,
      list1: [
        'Why does this metric matter?',
        'What action should be taken?',
        'What is the business implication?'
      ],
      midText: `Good storytelling transforms visuals into decisions. In practice, it means:`,
      list2: [
        'Highlighting actionable insights',
        'Removing noise',
        'Designing narrative flow',
        'Aligning visuals to strategic questions'
      ],
      footerText: `Professionals who master storytelling drive influence across teams.`,
      image: 'assets/resources/blog/data-visualization-skills-2026/data_storytelling.png'
    },

    {
      id: 'ux',
      title: 'UX & Visual Hierarchy',
      icon: 'assets/resources/blog/data-visualization-skills-2026/toc-icons/UX_Visual Hierarchy.svg',
      description: `Design principles are becoming non-negotiable. Here are 4 must-follow UX rules for 2026:`,
      list1: [
        'Clarity Over Decoration: If users cannot understand your visual in seconds, it fails.',
        'Visual Hierarchy: The most important insight must be the most dominant element.',
        'Reduce Cognitive Load: Make stakeholders think less. Insight should feel obvious.',
        'AI-Scalable Design: Build dashboards structured in a way that AI can query and extend them.'
      ],
      footerText: `Design is no longer aesthetic; it is strategic architecture. If a stakeholder cannot understand the insight in seconds, the design needs improvement.`,
      image: 'assets/resources/blog/data-visualization-skills-2026/ux_visualhierarchy.png'
    },

    {
      id: 'business',
      title: 'Business Domain Awareness',
      icon: 'assets/resources/blog/data-visualization-skills-2026/toc-icons/BusinessDomainAwareness.svg',
      description: `For professionals already in analytics, the highest ROI skill isn’t another tool. It’s strategic thinking. Understanding business context allows professionals to:`,
      list1: [
        'Design meaningful KPIs',
        'Interpret patterns correctly',
        'Avoid misleading analysis',
        'Provide executive-ready insights'
      ],
      footerText: `Domain awareness enhances credibility and impact. Soft skills now drive career growth as much as technical skills.`,
      image: 'assets/resources/blog/data-visualization-skills-2026/business_domain_awarness.png'
    },

    {
      id: 'automation',
      title: 'Automation Over Manual Effort',
      icon: 'assets/resources/blog/data-visualization-skills-2026/toc-icons/AutomationOverManualEffort.svg',
      description: `An uncomfortable truth: Manual measure writing, repetitive data cleaning, and basic prototyping have become less valuable and are becoming automated. AI now automates:`,
      list1: [
        'Visual suggestions',
        'Basic measure writing',
        'Data cleaning'
      ],
      midText: `What won’t be automated?`,
      list2: [
        'Business insight generation',
        'Strategic framing',
        'Context-driven analysis'
      ],
      footerText: `The skill <a href="https://www.kalpitatechnologies.com/tech-blogs/agentic-ai-in-sdlc" target="_blank" class="bold-link">shift is toward automation</a> design, not repetitive execution. Professionals who rely solely on manual effort risk becoming obsolete.`,
      image: 'assets/resources/blog/data-visualization-skills-2026/automation_over_manualeffort.png'
    },

    {
      id: 'decision',
      title: 'Decision Architecture (The Hidden Superpower)',
      icon: 'assets/resources/blog/data-visualization-skills-2026/toc-icons/DecisionArchitecture.svg',
      description: `Decision architecture in analytics is becoming the defining skill of 2026. Only few resumes mention this. It involves:`,
      list1: [
        'Structuring dashboards around executive priorities',
        'Aligning KPIs to business strategy',
        'Designing insights that drive action',
        'Building scalable reporting ecosystems'
      ],
      footerText: `Decision architecture is what elevates analysts into architects.`,
      image: 'assets/resources/blog/data-visualization-skills-2026/decision_architecture.png'
    }

  ];

  careerGuidanceSection = {
    heading: 'WHAT IF YOU FEEL BEHIND IN 2026?',

    description: `If you feel behind in data visualization in 2026, don’t try to master everything at once, start small and build momentum. Pick one messy dashboard, redesign a single page, simplify the layout, and focus on extracting one clear business decision it should support. This practical approach helps you shift from simply presenting data to enabling action. Consistent, incremental improvements compound over time, and real progress begins when you start thinking like a decision architect rather than just a report builder.`,

    trendHeading: 'THE BIG PICTURE: WHERE DATA VISUALIZATION IS HEADED',

    trends: [
      'AI-assisted',
      'Decision-focused',
      'Strategy-driven',
      'UX-conscious',
      'Business-aligned'
    ],

    highlightText: `The professionals who thrive will combine: Technical depth + AI fluency + Design thinking + Strategic storytelling.`,

    ctaText: 'latest blogs'
  };

  constructor(private router: Router, private activatedRoute: ActivatedRoute,
    private renderer: Renderer2,
    @Inject(DOCUMENT) private _document: Document
  ) { }

  ngOnInit(): void {
    this.injectSchemaMarkup();
    this.router.events.pipe(
      filter((event: any) => event instanceof NavigationEnd),
      map(() => this.activatedRoute),
      map((route) => {
        while (route.firstChild) route = route.firstChild;
        return route;
      }),
      filter((route) => route.outlet === 'primary'),
      mergeMap((route: { data: any; }) => route.data)
    )
  }

  ngOnDestroy(): void {
    this.removeSchemaMarkup();
  }

  removeSchemaMarkup(): void {
    if (this.jsonLdScript) {
      this.renderer.removeChild(this._document.head, this.jsonLdScript);
      this.jsonLdScript = null;
    }
  }

  injectSchemaMarkup(): void {
    const dataVizSchema = {
      "@context": "https://schema.org",
      "@graph": [
        {
          "@type": "WebPage",
          "@id": "https://www.kalpitatechnologies.com/tech-blogs/data-visualization-skills-2026",
          "url": "https://www.kalpitatechnologies.com/tech-blogs/data-visualization-skills-2026",
          "name": "Top 10 Data Visualization Skills You Need in 2026 to Stay Ahead of AI",
          "description": "Master the top data visualization skills in 2026 - AI in data visualization, SQL & query optimization, semantic modeling in analytics & decision architecture.",
          "inLanguage": "en-US",
          "datePublished": "2026-04-29",
          "dateModified": "2026-04-29",
          "speakable": {
            "@type": "SpeakableSpecification",
            "cssSelector": [".padding-headningmain", ".softwaredev-heading", ".section-title"]
          },
          "isPartOf": { "@type": "WebSite", "@id": "https://www.kalpitatechnologies.com/#website" }
        },
        {
          "@type": "BreadcrumbList",
          "@id": "https://www.kalpitatechnologies.com/tech-blogs/data-visualization-skills-2026#breadcrumb",
          "itemListElement": [
            { "@type": "ListItem", "position": 1, "item": { "@type": "WebPage", "@id": "https://www.kalpitatechnologies.com/", "name": "Home" } },
            { "@type": "ListItem", "position": 2, "item": { "@type": "WebPage", "@id": "https://www.kalpitatechnologies.com/tech-blogs/blog", "name": "Blog" } },
            { "@type": "ListItem", "position": 3, "item": { "@type": "WebPage", "@id": "https://www.kalpitatechnologies.com/tech-blogs/data-visualization-skills-2026", "name": "Top 10 Data Viz Skills 2026" } }
          ]
        },
        {
          "@type": "BlogPosting",
          "@id": "https://www.kalpitatechnologies.com/tech-blogs/data-visualization-skills-2026#article",
          "headline": "Top 10 Data Visualization Skills You Need in 2026 to Stay Ahead of AI",
          "articleBody": "Data visualization in 2026 is decision-driven, not report-driven. build technical depth (advanced SQL, scripting, semantic modeling), pair it with AI literacy and prompt intelligence...",
          "author": { "@type": "Organization", "name": "Kalpita Technologies Editorial Team" },
          "publisher": {
            "@type": "Organization",
            "name": "Kalpita Technologies",
            "logo": { "@type": "ImageObject", "url": "https://www.kalpitatechnologies.com/assets/common/black-logo.png" }
          },
          "image": "https://www.kalpitatechnologies.com/assets/resources/blog/data-visualization-skills-2026/data-visualization-skills-banner.png"
        },
        {
          "@type": "HowTo",
          "name": "How to Future-Proof Your Data Visualization Career",
          "step": [
            { "@type": "HowToStep", "name": "Technical Base", "text": "Master advanced SQL window functions and Python or JavaScript data libraries." },
            { "@type": "HowToStep", "name": "Semantic Modeling", "text": "Design star/snowflake schemas and KPI hierarchies for AI reliability." },
            { "@type": "HowToStep", "name": "AI Literacy", "text": "Use AI daily to prototype dashboards and craft prompt-driven analytics." },
            { "@type": "HowToStep", "name": "Decision Architecture", "text": "Structure every chart based on a specific business action stakeholders must take." },
            { "@type": "HowToStep", "name": "Domain Storytelling", "text": "Deepen domain knowledge to speak the business's language via narratives." }
          ]
        },
        {
          "@type": "FAQPage",
          "mainEntity": [
            {
              "@type": "Question",
              "name": "Will AI replace data visualization professionals?",
              "acceptedAnswer": { "@type": "Answer", "text": "No. AI automates routine dashboard work, but humans are essential for framing questions and translating data into action." }
            }
          ]
        },
        {
          "@type": "Organization",
          "@id": "https://www.kalpitatechnologies.com/#organization",
          "name": "Kalpita Technologies",
          "url": "https://www.kalpitatechnologies.com/"
        },
        {
          "@type": "LocalBusiness",
          "@id": "https://www.kalpitatechnologies.com/#localbusiness",
          "name": "Kalpita Technologies — Austin Office",
          "address": {
            "@type": "PostalAddress",
            "streetAddress": "Suite #300, 3rd Floor, Building 2, 1250 South Capital of Texas Highway",
            "addressLocality": "Austin",
            "addressRegion": "TX",
            "postalCode": "78746",
            "addressCountry": "US"
          },
          "telephone": "+1-512-240-2484",
          "url": "https://www.kalpitatechnologies.com/"
        }
      ]
    };

    this.jsonLdScript = this.renderer.createElement('script');
    this.jsonLdScript.type = 'application/ld+json';
    this.jsonLdScript.text = JSON.stringify(dataVizSchema);
    this.renderer.appendChild(this._document.head, this.jsonLdScript);
  }

  nav(url: any) {
    this.router.navigateByUrl(url)
  }

  @ViewChild('tableresponsiveImage', { static: false }) image!: ElementRef<HTMLImageElement>;

  originalWidth = 1920;
  originalHeight = 797;

  ngAfterViewInit(): void {
    this.resizeImageMap();
    this.image.nativeElement.onload = () => this.resizeImageMap();
  }

  @HostListener('window:resize')
  resizeImageMap(): void {
    const img = this.image.nativeElement;
    if (!img) return;

    const wRatio = img.clientWidth / this.originalWidth;
    const hRatio = img.clientHeight / this.originalHeight;

    document.querySelectorAll<HTMLAreaElement>('map[name="table-image-map"] area')
      .forEach(area => {
        const rawCoords =
          area.getAttribute('data-original-coords')
          || area.getAttribute('coords')
          || '';
        if (!rawCoords) return;

        area.setAttribute('data-original-coords', rawCoords);
        const scaled = rawCoords
          .split(',')
          .map((c, i) =>
            Math.round(
              Number(c) * (i % 2 === 0 ? wRatio : hRatio)
            )
          )
          .join(',');

        area.setAttribute('coords', scaled);
      });
  }

  scrollToSection(sectionId: string): void {
    const el = document.getElementById(sectionId);
    if (!el) return;
    const yOffset = 5 * 16;
    const top =
      el.getBoundingClientRect().top + window.pageYOffset - yOffset;
    window.scrollTo({ top, behavior: 'smooth' });
  }
}
